import { extractAdminTokens, getStoredAdminSession } from "./admin-login-api";

export interface YatraPdf { id: number; pdf: string }

async function request(suffix = "", init: RequestInit = {}, publicRead = false): Promise<unknown> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "");
  if (!base) { throw new Error("API base URL is not configured."); }
  const tenant = import.meta.env.VITE_X_TENANTID as string | undefined;
  const access = publicRead ? null : extractAdminTokens(getStoredAdminSession()?.session).access;
  const response = await fetch(`${base}/jain_gaurav_yatra/jain-yatra-pdf/${suffix}`, {
    ...init,
    credentials: publicRead ? "omit" : "include",
    headers: {
      ...(tenant ? { "X-Tenant-ID": tenant } : {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
  });
  if (response.status === 204) { return null; }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.detail || data?.message || (Array.isArray(data?.pdf) ? data.pdf.join(" ") : data?.pdf);
    throw new Error(typeof message === "string" ? message : `Request failed (${response.status}). Please try again.`);
  }
  return data;
}

function toPdf(value: unknown): YatraPdf {
  const row = value as Partial<YatraPdf> | null;
  if (!row || typeof row.id !== "number" || typeof row.pdf !== "string") {
    throw new Error("The server returned an unexpected document response.");
  }
  return { id: row.id, pdf: row.pdf };
}

export async function listYatraPdfs(signal?: AbortSignal, publicRead = false): Promise<YatraPdf[]> {
  const data = await request("", { signal }, publicRead);
  if (data === null) { return []; }
  if (Array.isArray(data)) { return data.map(toPdf); }
  const page = data as { results?: unknown[] };
  if (Array.isArray(page.results)) { return page.results.map(toPdf); }
  return [toPdf(data)];
}

export async function saveYatraPdf(file: File, id?: number): Promise<void> {
  const body = new FormData();
  body.append("pdf", file);
  await request(id === undefined ? "" : `${id}/`, {
    method: id === undefined ? "POST" : "PATCH",
    body,
  });
}

export async function deleteYatraPdf(id: number): Promise<void> {
  await request(`${id}/`, { method: "DELETE" });
}

export function documentUrl(value: string): string | null {
  try {
    const url = new URL(value, import.meta.env.VITE_API_BASE_URL as string);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch { return null; }
}
